import { useState } from "react";

import input from "../../../assets/styles/components/input.module.css";
import Button from "../../../components/Button/Button";
import styles from "./Suppliers.module.css";

import { Add, Delete, Search, ArrowBack, ArrowDropDown, ArrowDropUp, FilterAlt, FilterAltOffSharp, WhatsApp, Edit } from "@mui/icons-material";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteSupplier, loadAllSuppliers } from "../../../services/suppliers";
import type { IResult } from "../../../types/suppliers";
import TableContainer from "../../../components/TableContainer/TableContainer";
import Filter from "./Filter";
import ModalGeneric from "../../../components/ModalGeneric/ModalGeneric";
import { useIsMobile } from "../../../utils/useIsMobile";
import ModalDelete from "../../../components/ModalDelete/ModalDelete";
import InputSearch from "../../../components/InputSearch/InputSearch";

interface IOrderTable {
  nome_fantasia?: "ASC" | "DESC";
  estado?: "ASC" | "DESC";
  municipio?: "ASC" | "DESC";
  telefone?: "ASC" | "DESC";
}

const Suppliers = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [deleteTarget, setDeleteTarget] = useState<{ uid: string; nome: string } | null>(null);

  const [orderTable, setOrderTable] = useState<IOrderTable>({
    nome_fantasia: "ASC",
  });

  const [isHidden, setIsHidden] = useState(false);

  const [filter, setFilter] = useState({
    estado: "",
    municipio: "",
    telefone: "",
  });

  const [searchTerm, setSearchTerm] = useState("");

  const { data, fetchNextPage, hasNextPage } = useInfiniteQuery<IResult>({
    queryKey: ["suppliers", searchTerm, orderTable, filter],
    queryFn: ({ pageParam = 1 }) =>
      loadAllSuppliers(searchTerm, pageParam as number, orderTable, filter),
    getNextPageParam: (lastPage, allPages) => {
      const { data } = lastPage;

      if (data.hasMore) {
        return allPages.length + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 0,
  });

  const onSubmit = (query: string) => {
    setSearchTerm(query);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async (uid_fornecedor: string) => {
      const res = await deleteSupplier(uid_fornecedor);
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });

      toast.success(res.message);
      setDeleteTarget(null);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDelete = (supplier: any) => {
    setDeleteTarget({ 
      uid: supplier.uid_fornecedor, 
      nome: supplier.nome_fantasia 
    });
  };

  function formatWhatsapp(phone: string): string {
    const digits = phone.replace(/\D/g, "");
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  }

  const handleChangeOrder = (field: keyof typeof orderTable) => {
    setOrderTable({
      [field as keyof typeof orderTable]:
        orderTable[field] === "ASC" ? "DESC" : "ASC",
    });
  };

  const renderSortableHeader = (
    field: keyof typeof orderTable,
    label: string,
  ) => {
    return (
      <Button
        variant="textButton"
        onClick={() => {
          handleChangeOrder(field);
        }}
        title={orderTable[field] === "DESC" ? "Decrescente" : "Crescente"}
      >
        {label}
        {orderTable[field] ? (
          orderTable[field] === "DESC" ? (
            <ArrowDropDown />
          ) : (
            <ArrowDropUp />
          )
        ) : (
          ""
        )}
      </Button>
    );
  };

  return (
    <div className={styles.containerProjects}>
      <div className={styles.purchases}>
        <div className={styles.header}>
          <h1>Fornecedores</h1>
          
          <InputSearch
            searchValue={searchTerm}
            setSearchValue={setSearchTerm}
            placeholder="Pesquisar pela Razão Social ou Nome Fantasia"
          />

          <div className={styles.headerButtons}>
            <p>
              Qtd: <span>{data?.pages.at(-1)?.data.qtdSuppliers || 0}</span>
            </p>
            <Button
              variant="contain"
              onClick={() => setIsHidden(true)}
              title="Filtro"
            >
              <FilterAlt />
            </Button>
            <Button
              variant="contain"
              onClick={() =>
                setFilter({
                  estado: "",
                  municipio: "",
                  telefone: "",
                })
              }
              title="Limpar filtro"
            >
              <FilterAltOffSharp />
            </Button>
            <Button
              variant="contain"
              onClick={() => navigate(`add`)}
              title="Adicionar novo fornecedor"
            >
              <Add />
            </Button>
          </div>
        </div>
        {isMobile ? (
          // ── Mobile: cards ──────────────────────────────────────────────────
          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "0 4px" }}>
            {data?.pages?.flatMap((p) =>
              p.data.suppliers.map((supplier) => (
                <div key={supplier.uid_fornecedor}
                  style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", border: "1px solid #eee", cursor: "pointer" }}
                  onClick={() => navigate(`edit/${supplier.uid_fornecedor}`)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "#222" }}>{supplier.nome_fantasia}</span>
                  </div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                    {supplier.cidade?.municipio && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <span style={{ fontSize: "0.7rem", color: "#999", textTransform: "uppercase", fontWeight: 600 }}>Cidade</span>
                        <span style={{ fontSize: "0.88rem" }}>{supplier.cidade.municipio} / {supplier.estado?.estado}</span>
                      </div>
                    )}
                    {supplier.telefone && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <span style={{ fontSize: "0.7rem", color: "#999", textTransform: "uppercase", fontWeight: 600 }}>Telefone</span>
                        <span style={{ fontSize: "0.88rem" }}>{supplier.telefone}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #f0f0f0", paddingTop: 10 }}
                    onClick={(e) => e.stopPropagation()}>
                    <Button variant="contain" typeStyle="small" title="WhatsApp"
                      disabled={!supplier.whatsapp || supplier.whatsapp.trim() === ""}
                      onClick={() => supplier.whatsapp && window.open(`https://wa.me/${supplier.whatsapp}`)}>
                      <WhatsApp fontSize="small" />
                    </Button>
                    <Button variant="contain" typeStyle="small" title="Editar"
                      onClick={() => navigate(`edit/${supplier.uid_fornecedor}`)}>
                      <Edit fontSize="small" />
                    </Button>
                    <Button variant="contain" typeStyle={["small", "cancel"] as any} title="Excluir"
                      disabled={isPending}
                      onClick={() => handleDelete(supplier)}>
                      <Delete fontSize="small" />
                    </Button>
                  </div>
                </div>
              )) ?? []
            ) ?? []}
          </div>
        ) : (
          // ── Desktop: tabela ─────────────────────────────────────────────────
          <TableContainer
            columns={[
              { key: "nomeEx", titulo: renderSortableHeader("nome_fantasia", "Nome Fantasia") as any, classStyle: styles.columnNome },
              { key: "estadoEx", titulo: renderSortableHeader("estado", "Estado") as any, classStyle: styles.columnEstado },
              { key: "municipioEx", titulo: renderSortableHeader("municipio", "Município") as any, classStyle: styles.columnMunicipio },
              { key: "telefoneEx", titulo: renderSortableHeader("telefone", "Telefone") as any, classStyle: styles.columnTelefone },
              { key: "actionEx", titulo: "Ação", classStyle: styles.columnAction },
            ]}
            content={
              data?.pages?.flatMap(
                (p) =>
                  p.data.suppliers.map((supplier) => ({
                    values: {
                      nomeEx: supplier.nome_fantasia,
                      estadoEx: supplier?.estado?.estado,
                      municipioEx: supplier?.cidade?.municipio,
                      telefoneEx: supplier.telefone,
                    },
                    onClick: () => navigate(`edit/${supplier.uid_fornecedor}`),
                    buttons: [
                      {
                        variant: "contain", typeStyle: "small",
                        title: supplier.whatsapp && supplier.whatsapp.trim() !== "" ? `WhatsApp: ${formatWhatsapp(supplier.whatsapp)}` : "Sem WhatsApp",
                        disabled: !supplier.whatsapp || supplier.whatsapp.trim() === "",
                        onClick: () => supplier.whatsapp && supplier.whatsapp.trim() !== "" && window.open(`https://wa.me/${supplier.whatsapp}`),
                        icon: <WhatsApp />,
                      },
                      {
                        variant: "contain", typeStyle: "small", title: "Editar fornecedor",
                        onClick: () => navigate(`edit/${supplier.uid_fornecedor}`),
                        icon: <Edit />,
                      },
                      {
                        variant: "contain", typeStyle: ["small", "cancel"], title: "Deletar fornecedor",
                        onClick: () => handleDelete(supplier),
                        disabled: isPending,
                        icon: <Delete />,
                      },
                    ],
                  })) ?? [],
              ) ?? []
            }
          />
        )}

        <div className={styles.loadMore}>
          {hasNextPage ? (
            <Button variant="contain" onClick={() => fetchNextPage()}>
              Carregar Mais registros
            </Button>
          ) : (
            <p className={styles.tableLastItem}>
              Todos os registros foram carregados
            </p>
          )}
        </div>
      </div>
      {isHidden && (
        <Filter
          dataFilter={filter}
          handleDataFilter={(value, key) =>
            setFilter((prev) => ({ ...prev, [key]: value }))
          }
          onClose={() => setIsHidden(false)}
        />
      )}

      <ModalDelete
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && mutate(deleteTarget.uid)}
        isDeleting={isPending}
        entity="fornecedor"
        title={deleteTarget?.nome || ""}
        isMobile={isMobile}
      />
    </div>
  );
};

export default Suppliers;